import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useGlobalMutation, useGlobalQuery } from '@/state/query-client/hooks';
import { useToast } from '@/hooks/use-toast';
import {
  createSkillSubCategory,
  deleteSkillSubCategory,
  getSkillSubCategories,
  updateSkillSubCategory,
} from '../services/skill-sub-category.service';
import {
  GetSkillSubCategoriesParams,
  SkillSubCategoryInsertInput,
  SkillSubCategoryUpdateInput,
} from '../types/skill-sub-category.types';

const invalidateSkillSubCategoryQueries = (queryClient: ReturnType<typeof useQueryClient>) =>
  queryClient.invalidateQueries({ queryKey: ['hr-skill-sub-categories'] });

export const useGetSkillSubCategories = (params: GetSkillSubCategoriesParams) => {
  return useGlobalQuery({
    queryKey: ['hr-skill-sub-categories', params],
    queryFn: () => getSkillSubCategories(params),
    staleTime: 5 * 60 * 1000,
  });
};

export const useCreateSkillSubCategory = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (input: SkillSubCategoryInsertInput) => createSkillSubCategory(input),
    onSuccess: () => {
      invalidateSkillSubCategoryQueries(queryClient);
      toast({ title: t('SKILL_SUB_CATEGORY_CREATED') });
    },
  });
};

export const useUpdateSkillSubCategory = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (params: { itemId: string; input: SkillSubCategoryUpdateInput }) =>
      updateSkillSubCategory(params),
    onSuccess: () => {
      invalidateSkillSubCategoryQueries(queryClient);
      toast({ title: t('SKILL_SUB_CATEGORY_UPDATED') });
    },
  });
};

export const useDeleteSkillSubCategory = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation();

  return useGlobalMutation({
    mutationFn: (itemId: string) => deleteSkillSubCategory(itemId),
    onSuccess: () => {
      invalidateSkillSubCategoryQueries(queryClient);
      toast({ title: t('SKILL_SUB_CATEGORY_DELETED') });
    },
  });
};
